/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import {
    AviPermissionResource,
    ITrustedHostProfile,
} from 'generated-types';

import { TrustedHostProfile } from 'object-types';
import { ObjectTypeItem } from 'object-type-item';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { Component, Type } from '@angular/core';
import { TrustedHostConfigItem } from 'ng/modules/message-items/factories';
import { RepeatedMessageItem } from 'ajs/modules/data-model/factories';

interface ITrustedHostProfileConfig extends Omit<ITrustedHostProfile, 'hosts'> {
    hosts: RepeatedMessageItem<TrustedHostConfigItem>;
}

interface ITrustedHostProfileData {
    config: ITrustedHostProfileConfig;
}

/**
 * @description Trusted Host Profile Item.
 * @author Manideep Kotha, Rachit Aggarwal, Shahab Hashmi
 */
export class TrustedHostProfileItem extends withEditChildMessageItemMixin(
    withFullModalMixin(ObjectTypeItem),
) {
    public data: ITrustedHostProfileData;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: TrustedHostProfile,
            objectName: 'trustedhostprofile',
            permissionName: AviPermissionResource.PERMISSION_SYSTEMCONFIGURATION,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Open Host header modal for adding a new Host.
     */
    public addHostHeader(): void {
        this.addChildMessageItem({
            field: 'hosts',
        });
    }

    /**
     * Open Host header modal for editing a host.
     */
    public editHostHeader(trustedHostConfigItem: TrustedHostConfigItem): void {
        this.editChildMessageItem({
            field: 'hosts',
            modalBindings: {
                isEditing: true,
            },
            messageItem: trustedHostConfigItem,
        });
    }

    /**
     * Import lazy-loaded modal component.
     */
    /* eslint-disable-next-line class-methods-use-this */
    public async getModalComponent(): Promise<Type<Component>> {
        const {
            TrustedHostProfileModalComponent,
        } = await import(
            /* webpackChunkName: "trusted-host-profile-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/trusted-host-profile-modal/trusted-host-profile-modal.component'
        );

        return TrustedHostProfileModalComponent as Type<Component>;
    }
}
